/**
 * If the current URL carries an `#lti-session=<base64url-json>` fragment, decode
 * it and write the Cognito tokens into the localStorage keys Amplify expects.
 * The fragment is stripped from the URL after consumption so it never lingers in
 * browser history or gets copied around.
 *
 * Runs at boot from `index.tsx` before React renders — that way `AuthProvider`'s
 * initial `validateSession()` picks up the tokens and lands the student straight
 * on the reading UI. Called synchronously; safe to invoke even when no fragment
 * is present.
 */
export function hydrateLtiSessionFromHash(): void {
  const hash = window.location.hash;
  if (!hash || !hash.includes('lti-session=')) return;

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const encoded = params.get('lti-session');
  if (!encoded) return;

  let payload: {
    userPoolClientId: string;
    cognitoSub: string;
    idToken: string;
    accessToken: string;
    refreshToken: string;
  };
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    payload = JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch {
    console.warn('Failed to decode lti-session fragment; ignoring.');
    stripHash();
    return;
  }

  const { userPoolClientId, cognitoSub, idToken, accessToken, refreshToken } =
    payload;
  if (
    !userPoolClientId ||
    !cognitoSub ||
    !idToken ||
    !accessToken ||
    !refreshToken
  ) {
    console.warn('lti-session fragment is missing required fields; ignoring.');
    stripHash();
    return;
  }

  const prefix = `CognitoIdentityServiceProvider.${userPoolClientId}.`;
  const clockDrift = computeClockDrift(idToken, accessToken);
  localStorage.setItem(`${prefix}LastAuthUser`, cognitoSub);
  localStorage.setItem(`${prefix}${cognitoSub}.idToken`, idToken);
  localStorage.setItem(`${prefix}${cognitoSub}.accessToken`, accessToken);
  localStorage.setItem(`${prefix}${cognitoSub}.refreshToken`, refreshToken);
  localStorage.setItem(`${prefix}${cognitoSub}.clockDrift`, String(clockDrift));

  stripHash();
}

function computeClockDrift(idToken: string, accessToken: string): number {
  const now = Math.floor(Date.now() / 1000);
  const iat = Math.min(iatOf(idToken) ?? now, iatOf(accessToken) ?? now);
  return now - iat;
}

function iatOf(token: string): number | undefined {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return undefined;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.iat === 'number' ? payload.iat : undefined;
  } catch {
    return undefined;
  }
}

function stripHash(): void {
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}`);
}
